<template>
  <div
    :id="id"
    :class="_container_class"
    :tabindex="errors && errors.length > 0 ? '-1' : undefined"
  >
    <div v-if="!raw" :class="_attr_class">
      <div :id="id ? id + '_lbl' : null" :class="_label_class">
        {{ label }}
        <slot name="after-label"></slot>
        <span v-if="compressed" role="presentation">{{ spacer }}</span>
      </div>
      <div
        v-if="value"
        :class="_value_class"
        :lang="language"
        :xml:lang="language"
      >
        {{ value }}
        <cat-icon
          v-if="icon"
          :decorative="!icon.label"
          :class="icon.css"
          :icon="icon.icon"
          :icon-label="icon.label"
        />
      </div>
      <div v-else :class="_value_class">
        <slot>{{ resolveTxt("skin.readonly.no.value", "Not specified") }}</slot>
      </div>
      <slot name="after-value"></slot>
    </div>
    <span
      v-if="raw && !conditional"
      :class="_value_class"
      role="note"
      :aria-label="label"
      :title="label"
      ><slot>{{ value }}</slot>
    </span>
    <cat-conditional-group
      v-if="raw && conditional"
      role="note"
      :aria-label="label"
      :title="label"
      ><slot>{{ value }}</slot>
    </cat-conditional-group>
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <cat-errors v-if="errors" :errors="errors" />
    <div role="presentation" class="spacer"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import { hasSlotContent } from "@/components/utilities/slot-content.js";
import CatConditionalGroup from "@/components/fieldsets/conditional-group.vue";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatIcon from "@/components/utilities/catIcon.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatAttributeReadonly",
  components: {
    CatHelp,
    CatConditionalGroup,
    CatErrors,
    CatIcon
  },
  props: {
    id: {
      type: [Boolean, Number, String],
      description: "Sets the ID attributes for child elements."
    },
    value: {
      type: [String, Number],
      description: "Defines the initial default value for the associated field."
    },
    label: {
      type: String,
      description: "Sets the label to display above input fields."
    },
    // the following add classes, but do not appear to be used
    raw: {
      type: [Boolean, String],
      default: false
    },
    preformatted: {
      type: [Boolean, String],
      default: false
    },
    conditional: {
      type: [Boolean],
      default: false
    },
    compressed: {
      type: [Boolean, String],
      default: false
    },
    labelSize: {
      type: String,
      description:
        "The label width of the readonly attribute. Supported value are medium (60%) and large (80%)."
    },
    language: {
      type: String,
      description:
        "ISO 639-2 language code to be specified in the attribute value."
    },
    errors: {
      type: [Array],
      description:
        "Sets the error message to be shown on invalid input with the following format: { level: 0, msg: 'This field can not be empty' } where level defines the error level and msg defines the message shown."
    },
    help: {
      type: Object,
      description: "Sets the expandable Help Text above an input field."
    },
    icon: {
      type: Object,
      description:
        "Object with the following properties - icon, message, css. Eg. { icon: 'check', label: 'Success', css: 'text-success mrt' }"
    }
  },

  computed: {
    _label_class: function () {
      return {
        empty: !this.label,
        dt: true
      };
    },
    _attr_class: function () {
      return {
        dl: true,
        "readonly-list": true,
        compressed: this.compressed
      };
    },
    _container_class: function () {
      let css = {
        "cat-attribute-readonly-wrapper": true,
        invalid: this.errors && this.errors.length > 0
      };
      if (this.labelSize) {
        css["label-" + this.labelSize] = true;
      }
      return css;
    },
    spacer: function () {
      return typeof this.compressed == "string" ? this.compressed : ":";
    },
    _value_class: function () {
      return {
        dd: true,
        value: true,
        pre: this.preformatted
      };
    }
  }
});
</script>
